import { useMemo } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { capitalize } from 'lodash-es';
import { RRule, Weekday } from 'rrule';
import { useTranslation, i18next } from '@frontend/i18n';
import { ordinalSuffix } from '@frontend/string';
import { theme } from '@frontend/theme';
import {
  DatePickerField,
  DaysOfWeek,
  DropdownField,
  FieldChangeEvent,
  RadioCardField,
  Text,
  TimeField,
} from '@frontend/design-system';
import { useRuleRecurrenceForm } from '../../../../hooks/settings/use-rule-recurrence-form';
import {
  DayMapKey,
  daysStringMap,
  getRRuleDayFromDaysOfWeek,
  getWeekdayFromByWeekDay,
  weekNumberString,
} from '../../../../rrule-helper';
import {
  AvailabilityRuleFormRow,
  AvailabilityRuleFormSection,
  DayPickerWithSelectedDays,
  PreviewNotification,
  RadioCardFieldRow,
} from '../atoms';
import {
  NthDayDropdown,
  NthMonthDropdown,
  NthWeekDropdown,
  NthWeekStringDropdown,
  WeekdayDropdown,
} from '../molecules';
import {
  availabilityRecurrenceOptions,
  AvailabilityRecurrenceTypes,
  GetMonthlyRecurrenceFormFieldProps,
  GetRuleRecurrenceFormFieldProps,
  repeatFrequencyOptions,
} from '../types';

export type RRuleHandler = (rRule: RRule | undefined) => void;

type RuleRecurrenceFormProps = {
  locationId: string | undefined;
  ruleRecurrenceFormProps: ReturnType<typeof useRuleRecurrenceForm>;
};

export const RuleRecurrenceForm = ({ ruleRecurrenceFormProps }: RuleRecurrenceFormProps) => {
  const { t } = useTranslation('schedule-settings');

  const {
    recurrenceFormValues,
    recurrenceType,
    daysOfWeek,
    repeatOn,
    rRule,
    setDaysOfWeek,
    setRepeatOn,
    getRecurrenceFormFieldProps,
    getMonthlyRecurrenceFormFieldProps,
    getAvailabilityRuleFieldProps,
  } = ruleRecurrenceFormProps;

  const { repeatFrequencyType } = recurrenceFormValues;
  const handleDayToggle =
    (setState: React.Dispatch<React.SetStateAction<Weekday[]>>) =>
    ({ selectedDays }: { selectedDays: DaysOfWeek[] }) => {
      setState(selectedDays.map(getRRuleDayFromDaysOfWeek));
    };
  const previewPatternDescription = useMemo(
    () => getPreviewPatternDescription({ recurrenceType, repeatFrequencyType, rRule }),
    [recurrenceType, repeatFrequencyType, rRule]
  );

  return (
    <AvailabilityRuleFormSection title={t('Schedule')}>
      <AvailabilityRuleFormRow>
        <DropdownField label={t('Recurrence')} {...getAvailabilityRuleFieldProps('recurrenceType')} hasNonFloatingLabel>
          {availabilityRecurrenceOptions?.map((item) => (
            <DropdownField.Option key={item.id} value={item.id}>
              {item.displayName}
            </DropdownField.Option>
          ))}
        </DropdownField>
      </AvailabilityRuleFormRow>
      {recurrenceType === 'SPECIFIC_DATE' && (
        <AvailabilityRuleFormRow>
          <DatePickerField
            label={t('Date')}
            {...getAvailabilityRuleFieldProps('startDate')}
            hasNonFloatingLabel
            css={{ maxWidth: 'none' }}
          />
        </AvailabilityRuleFormRow>
      )}
      {recurrenceType === 'WEEKLY' && (
        <AvailabilityRuleFormRow title={t('Day(s) of the week')}>
          <DayPickerWithSelectedDays daysPicked={daysOfWeek} onDayToggle={handleDayToggle(setDaysOfWeek)} />
        </AvailabilityRuleFormRow>
      )}
      {recurrenceType === 'CUSTOM' && (
        <>
          <AvailabilityRuleFormRow title={t('Repeat Pattern')} css={styles.repeatPatternRow}>
            <Text>{t('Every')}</Text>
            <RepeatFrequencyDropdown getFieldProps={getRecurrenceFormFieldProps} />
            <DropdownField label={''} {...getRecurrenceFormFieldProps('repeatFrequencyType')}>
              {repeatFrequencyOptions?.map((item) => (
                <DropdownField.Option key={item.id} value={item.id}>
                  {item.displayName}
                </DropdownField.Option>
              ))}
            </DropdownField>
          </AvailabilityRuleFormRow>
          {recurrenceFormValues.repeatFrequencyType === 'weeks' && (
            <AvailabilityRuleFormRow title={t('Repeat On')}>
              <DayPickerWithSelectedDays daysPicked={repeatOn} onDayToggle={handleDayToggle(setRepeatOn)} />
            </AvailabilityRuleFormRow>
          )}
          {recurrenceFormValues.repeatFrequencyType === 'months' && (
            <MonthlyRecurrenceRow getFieldProps={getMonthlyRecurrenceFormFieldProps} />
          )}
        </>
      )}
      {recurrenceType !== 'SPECIFIC_DATE' && (
        <AvailabilityRuleFormRow>
          <DatePickerField
            label={t('Start Date')}
            {...getAvailabilityRuleFieldProps('startDate')}
            hasNonFloatingLabel
          />
          <DatePickerField
            label={<DayPickerLabel />}
            {...getAvailabilityRuleFieldProps('endDate')}
            hasNonFloatingLabel
          />
        </AvailabilityRuleFormRow>
      )}
      <AvailabilityRuleFormRow>
        <TimeField label={t('Start Time')} {...getAvailabilityRuleFieldProps('startTime')} hasNonFloatingLabel />
        <TimeField label={t('End Time')} {...getAvailabilityRuleFieldProps('endTime')} hasNonFloatingLabel />
      </AvailabilityRuleFormRow>
      {recurrenceType === 'CUSTOM' && (
        <AvailabilityRuleFormRow>
          <PreviewNotification title={t('Pattern preview:')} description={previewPatternDescription} />
        </AvailabilityRuleFormRow>
      )}
    </AvailabilityRuleFormSection>
  );
};

const DayPickerLabel = () => {
  const { t } = useTranslation('schedule-settings');
  return (
    <>
      {t('End Date')}
      <em style={{ fontWeight: theme.font.weight.regular, color: theme.font.colors.subdued }}> ({t('optional')})</em>
    </>
  );
};

type MonthlyRecurrenceRowProps = {
  getFieldProps: GetMonthlyRecurrenceFormFieldProps;
};

const isChangeEvent = (
  event: FieldChangeEvent | undefined
): event is FieldChangeEvent & { target: { name: string } } => {
  return !!event && 'target' in event && 'name' in event.target;
};

const MonthlyRecurrenceRow = ({ getFieldProps }: MonthlyRecurrenceRowProps) => {
  const { t } = useTranslation('schedule-settings');
  const monthlyFrequencyTypeProps = getFieldProps('monthlyFrequencyType');
  const monthlyFrequencyType = monthlyFrequencyTypeProps.value;

  // The onChange handler for the monthly frequency type (radio) needs to check the event type
  // to avoid issues with the Dropdown fields within the radio option
  const handleChange = (e?: FieldChangeEvent) => {
    if (isChangeEvent(e)) {
      if (e?.target?.name !== monthlyFrequencyTypeProps.name) return;
    }
    monthlyFrequencyTypeProps.onChange?.(e);
  };

  return (
    <AvailabilityRuleFormRow>
      <RadioCardFieldRow label={''} {...monthlyFrequencyTypeProps} onChange={handleChange} hasNonFloatingLabel>
        <RadioCardField.Option
          isInteractive
          key={'day'}
          value={'day'}
          title={
            <div css={styles.monthlyFrequencyOption}>
              <Text css={styles.monthlyFrequencyText}>{t('On day')}</Text>
              <NthDayDropdown {...getFieldProps('monthlyFrequencyDay')} required={monthlyFrequencyType === 'day'} />
              <Text css={styles.monthlyFrequencyText}>{t('of the month')}</Text>
            </div>
          }
          description={''}
        />
        <RadioCardField.Option
          isInteractive
          key={'weekday'}
          value={'weekday'}
          title={
            <div css={styles.monthlyFrequencyOption}>
              <Text css={styles.monthlyFrequencyText}>{t('On the')}</Text>
              <NthWeekStringDropdown
                {...getFieldProps('monthlyFrequencyWeek')}
                css={{ maxWidth: '150px' }}
                required={monthlyFrequencyType === 'weekday'}
              />
              <WeekdayDropdown
                {...getFieldProps('monthlyFrequencyWeekday')}
                css={{ maxWidth: '200px' }}
                required={monthlyFrequencyType === 'weekday'}
              />
              <Text css={styles.monthlyFrequencyText}>{t('of the month')}</Text>
            </div>
          }
          description={''}
        />
      </RadioCardFieldRow>
    </AvailabilityRuleFormRow>
  );
};

export const RepeatFrequencyDropdown = ({ getFieldProps }: { getFieldProps: GetRuleRecurrenceFormFieldProps }) => {
  const repeatFrequencyType = getFieldProps('repeatFrequencyType').value;
  const repeatFrequencyFieldProps = getFieldProps('repeatFrequency');

  if (repeatFrequencyType === 'days') {
    return <NthDayDropdown {...repeatFrequencyFieldProps} />;
  }
  if (repeatFrequencyType === 'weeks') {
    return <NthWeekDropdown {...repeatFrequencyFieldProps} />;
  }
  if (repeatFrequencyType === 'months') {
    return <NthMonthDropdown {...repeatFrequencyFieldProps} />;
  }
  return null;
};

const weekdayOptions = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

const t = i18next.getFixedT(null, 'schedule-settings');
const getPreviewPatternDescription = ({
  recurrenceType,
  repeatFrequencyType,
  rRule,
}: {
  recurrenceType: AvailabilityRecurrenceTypes | undefined;
  repeatFrequencyType: string | undefined;
  rRule: RRule | undefined;
}) => {
  if (recurrenceType !== 'CUSTOM' || !rRule || !rRule.options.interval) return '';
  const frequency = rRule.options.interval;
  const timezone = rRule.options.tzid ?? undefined;
  let patternPrefix = '';
  let patternSuffix = '';
  let nextOccurrences = '';
  const displayCount = repeatFrequencyType === 'days' ? 3 : 6;
  const calculatedOccurrences = rRule.all((_, index) => index < displayCount + 1);
  const occurrences = calculatedOccurrences.slice(0, displayCount);
  const firstOccurrence = occurrences[0];
  const hasMoreOccurrences = (calculatedOccurrences.length ?? 0) > displayCount;
  const terminator = hasMoreOccurrences ? '...' : '.';
  if (repeatFrequencyType === 'days') {
    patternPrefix = frequency === 1 ? t('everyday') : t('every {{frequency}} days', { frequency });
    nextOccurrences = occurrences.map((date) => dayjs(date).tz(timezone).format('MMM D (ddd)')).join(', ');
  } else if (repeatFrequencyType === 'weeks') {
    patternPrefix = frequency === 1 ? t('every week') : t('every {{frequency}} weeks', { frequency });
    const weekList = rRule.options.byweekday?.map((weekday: number) => weekdayOptions[weekday]).join(', ') ?? '';
    patternSuffix = weekList ? t('on {{weekList}}', { weekList }) : '';
    nextOccurrences = occurrences.map((date) => dayjs(date).tz(timezone).format('MMM D')).join(', ');
  } else if (repeatFrequencyType === 'months') {
    patternPrefix = frequency === 1 ? t('every month') : t('every {{frequency}} months', { frequency });
    const byweekday = rRule.origOptions.byweekday;
    const byweekdayList = Array.isArray(byweekday) ? byweekday : byweekday ? [byweekday] : [];
    const firstByWeekday = byweekdayList.length > 0 ? byweekdayList[0] : undefined;
    if (byweekday) {
      const [weekday, weekNumber] = getWeekdayFromByWeekDay(firstByWeekday);
      const weekNumString = weekNumber ? weekNumberString[weekNumber] : '';
      const weekdayString = weekday ? daysStringMap[weekday.toString() as DayMapKey] : '';
      patternSuffix =
        weekday && weekNumber
          ? t('on {{n}} {{day}}', { n: capitalize(weekNumString), day: capitalize(weekdayString) })
          : '';
    } else {
      const startingDay = firstOccurrence ? ordinalSuffix(dayjs(firstOccurrence).tz(timezone).date()) : '';
      patternSuffix = startingDay ? t('starting on the {{day}}', { day: startingDay }) : '';
    }
    nextOccurrences = occurrences.map((date) => dayjs(date).tz(timezone).format('MMM D')).join(', ');
  }
  const pattern = (patternPrefix + ' ' + patternSuffix).trim();
  return patternPrefix ? (
    <>
      {t('Availability')} {pattern}.
      <br />
      {t('Next Occurences')}: {nextOccurrences}
      {terminator}
    </>
  ) : null;
};

const styles = {
  patientBookingRadioField: css`
    align-items: center;
  `,
  monthlyFrequencyOption: css`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: ${theme.spacing(1)};
  `,
  repeatPatternRow: css`
    display: flex;
    align-items: center;

    > * {
      flex-basis: 0;
      flex-shrink: 1;
    }
    > *:last-child {
      flex: 1;
    }
  `,
  monthlyFrequencyText: css`
    pointer-events: none;
  `,
};
